import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import {
  collectRolesFromMetadata,
  normalizeRoles,
  parseJwtPayload,
  PLATFORM_ROLES,
  STAFF_APP_ROLES,
  type AppRole,
} from "@/hooks/accessRoleUtils";
import {
  MODULES,
  ROLE_INFO,
  MODULE_INFO,
  type Module,
  type Action,
  canAccessModule as checkModuleAccess,
  getMergedPermissions,
  getRoleCapabilitySummary,
  hasPermission,
} from "@/lib/permissions";
import {
  type RoleScope,
  type ScopeEntityType,
  isRoleScopedToEntity,
  getScopedEntityIds,
} from "@/lib/role-scopes";
import { isSupabasePermissionError, isSupabaseSchemaError } from "@/lib/supabase-compat";

export type OrgRole = "owner" | "admin" | "member";
const JWT_TRUSTED_ORG_ROLES: AppRole[] = ["platform_super_admin", "platform_support", "super_admin"];

export interface MinistryMembership {
  id?: string;
  ministry_id: string;
  role?: string | null;
  is_leader?: boolean | null;
}

export interface AccessCapabilities {
  can: (module: Module, action: Action) => boolean;
  canAccessModule: (module: Module) => boolean;
  mergedPermissions: ReturnType<typeof getMergedPermissions>;
  accessibleModules: Module[];
  isOrgAdmin: boolean;
  isOrgOwner: boolean;
  hasAnyAppRole: boolean;
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  isPlatformSuperAdmin: boolean;
  isPlatformSupport: boolean;
  isPlatformReadonly: boolean;
  getRoleLabel: (role: AppRole) => string;
  getRoleDescription: (role: AppRole) => string;
  getModuleLabel: (module: Module) => string;
  getCapabilitySummary: () => string[];
  /** Raw scope rows for the user. Empty array = org-wide (no restrictions). */
  scopedEntities: RoleScope[];
  /** Check if any of the user's roles grant access to a specific entity. */
  canAccessEntity: (entityType: ScopeEntityType, entityId: string) => boolean;
  /** Get entity IDs the user is scoped to for a given type. null = org-wide. */
  getScopedIds: (entityType: ScopeEntityType) => string[] | null;
}

export interface AccessContext {
  org_id: string | null;
  org_role: OrgRole | null;
  app_roles: AppRole[];
  /**
   * app_roles filtered to the ACTIVE org (plus global null-org grants), mirroring
   * public.has_role_in_org. Use this — not app_roles — for org-scoped gates so a
   * role held only in a different org doesn't leak access here.
   */
  org_app_roles: AppRole[];
  /**
   * Same as org_app_roles but WITHOUT the JWT-metadata-trusted roles folded in —
   * only roles backed by an actual user_roles DB row. Use this for a gate whose
   * matching RLS policy queries user_roles directly rather than trusting JWT
   * metadata: org_app_roles can be true for a metadata-only grant (e.g. a
   * platform_super_admin claim with no DB row) that such a policy will still
   * reject, which lets the UI enable a write the server then silently fails or
   * no-ops on.
   */
  org_app_roles_db_only: AppRole[];
  /**
   * Same as org_app_roles_db_only but WITHOUT the global null-org grants: only user_roles rows
   * whose organization_id is the active org. Use this for a gate whose RLS policy matches
   * `organization_id IN (SELECT user_roles.organization_id ...)` (event_templates' writes, for
   * one), which a null-org row never satisfies.
   */
  org_only_app_roles_db_only: AppRole[];
  /**
   * Same relationship to app_roles that org_app_roles_db_only has to
   * org_app_roles: only roles backed by an actual user_roles DB row, global
   * (not org-filtered). has_any_role() — the Postgres function most RLS
   * policies call for a bare, non-org-scoped platform-role check (e.g.
   * contributions_select_authenticated's final OR branch) — reads only
   * public.user_roles, so a metadata-only super_admin/platform_support grant
   * with no DB row passes app_roles but is rejected by that check, letting a
   * gate built on app_roles enable a UI surface RLS then silently returns
   * less data for than it appears to (Codex P2, PR #6406, round 12).
   */
  app_roles_db_only: AppRole[];
  is_staff: boolean;
  ministry_memberships: MinistryMembership[];
  leader_ministries: string[];
  /** All ministries the user is a member of (any role, active status). */
  member_ministries: string[];
  capabilities: AccessCapabilities;
  /** Raw role scope rows. Empty = org-wide access (backward compatible). */
  scopedEntities: RoleScope[];
  /**
   * Whether `scopedEntities` reflects a SUCCESSFUL role_scopes lookup. False when
   * the query was denied/errored (other than the table simply not existing) — in
   * which case an empty `scopedEntities` is NOT trustworthy and security gates
   * must fail closed rather than treat the user as unrestricted.
   */
  scopedEntitiesReliable: boolean;
}

export const ACCESS_CONTEXT_QUERY_TIMEOUT_MS = 15_000;
export const ACCESS_CONTEXT_REFRESH_INTERVAL_MS = 30_000;

export interface AccessContextOptions {
  /** Verify on activation and refresh while an access-sensitive workspace is open. */
  refreshAccess?: boolean;
}

class AccessContextTimeoutError extends Error {
  constructor() {
    super("Timed out while loading access context");
    this.name = "AccessContextTimeoutError";
  }
}

const withAccessContextTimeout = async <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new AccessContextTimeoutError()),
      ACCESS_CONTEXT_QUERY_TIMEOUT_MS,
    );
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

const getRolesFromMetadata = (
  sessionToken: string | undefined,
  _userMetadata?: Record<string, unknown>,
  appMetadata?: Record<string, unknown>
): AppRole[] => {
  const tokenPayload = parseJwtPayload(sessionToken);
  const tokenAppMetadata = (tokenPayload?.app_metadata ?? {}) as Record<string, unknown>;

  // SECURITY: Only trust app_metadata for role decisions.
  // user_metadata is editable by the end user and MUST NOT be used
  // for authorization (Critical #9 remediation).
  // The _userMetadata parameter is kept for API compatibility but ignored.
  const combined = [
    ...collectRolesFromMetadata(appMetadata),
    ...collectRolesFromMetadata(tokenPayload ?? undefined),
    ...collectRolesFromMetadata(tokenAppMetadata),
  ];
  return normalizeRoles(combined);
};

const buildCapabilities = (
  roles: AppRole[],
  orgRole: OrgRole | null,
  scopes: RoleScope[] = [],
  organizationId: string | null = null,
): AccessCapabilities => {
  const hasRole = (role: AppRole) => roles.includes(role);
  const hasAnyRole = (checkRoles: AppRole[]) => checkRoles.some((role) => roles.includes(role));
  const isSuperAdmin = hasAnyRole(["platform_super_admin", "super_admin"]);
  const isPlatformAdmin = hasAnyRole([...PLATFORM_ROLES, "super_admin"]);
  const isPlatformSuperAdmin = hasAnyRole(["platform_super_admin", "super_admin"]);
  const isPlatformSupport = hasRole("platform_support");
  const isPlatformReadonly = hasRole("platform_readonly");
  const isOrgAdmin = orgRole === "owner" || orgRole === "admin";
  const isOrgOwner = orgRole === "owner";
  const hasAnyAppRole = roles.length > 0;
  const mergedPermissions = getMergedPermissions(roles);
  const accessibleModules = MODULES.filter((module) => checkModuleAccess(roles, module));

  const can = (module: Module, action: Action): boolean => {
    if (isSuperAdmin) return true;
    return hasPermission(roles, module, action);
  };

  const canAccessModule = (module: Module): boolean => {
    if (isSuperAdmin) return true;
    return checkModuleAccess(roles, module);
  };

  const getRoleLabel = (role: AppRole): string => {
    return ROLE_INFO[role]?.label ?? role;
  };

  const getRoleDescription = (role: AppRole): string => {
    return ROLE_INFO[role]?.description ?? "";
  };

  const getModuleLabel = (module: Module): string => {
    return MODULE_INFO[module]?.label ?? module;
  };

  const getCapabilitySummary = (): string[] => {
    const allCapabilities = new Set<string>();
    for (const role of roles) {
      const caps = getRoleCapabilitySummary(role);
      caps.forEach((cap) => allCapabilities.add(cap));
    }
    return Array.from(allCapabilities);
  };

  // ── Scope-aware helpers ──────────────────────────────────────────────
  // These reuse the existing functions from role-scopes.ts.
  // When scopes is empty, all helpers treat the user as org-wide (no restrictions).

  const canAccessEntity = (entityType: ScopeEntityType, entityId: string): boolean => {
    if (isSuperAdmin) return true;
    if (scopes.length === 0) return true; // no scope rows at all → org-wide
    const orgId = organizationId ?? "";
    // Check if ANY of the user's roles grant access to this entity
    return roles.some((role) =>
      isRoleScopedToEntity(scopes, role, orgId, entityType, entityId)
    );
  };

  const getScopedIds = (entityType: ScopeEntityType): string[] | null => {
    if (isSuperAdmin) return null; // super admins always org-wide
    if (scopes.length === 0) return null; // no scopes = org-wide
    const orgId = organizationId ?? "";
    // Merge scoped entity IDs across all roles
    const allIds = new Set<string>();
    let anyRoleHasScopes = false;
    for (const role of roles) {
      const ids = getScopedEntityIds(scopes, role, orgId, entityType);
      if (ids !== null) {
        anyRoleHasScopes = true;
        ids.forEach((id) => allIds.add(id));
      }
    }
    // If no role has scopes for this entity type → org-wide
    if (!anyRoleHasScopes) return null;
    return Array.from(allIds);
  };

  return {
    can,
    canAccessModule,
    mergedPermissions,
    accessibleModules,
    isOrgAdmin,
    isOrgOwner,
    hasAnyAppRole,
    isSuperAdmin,
    isPlatformAdmin,
    isPlatformSuperAdmin,
    isPlatformSupport,
    isPlatformReadonly,
    getRoleLabel,
    getRoleDescription,
    getModuleLabel,
    getCapabilitySummary,
    scopedEntities: scopes,
    canAccessEntity,
    getScopedIds,
  };
};

const isLeaderMembership = (membership: MinistryMembership) => {
  if (membership.is_leader) return true;
  if (!membership.role) return false;
  return ["leader", "scheduler"].includes(membership.role);
};

/** Core access context query — org membership, roles, person lookup (parallel). */
interface CoreAccessData {
  orgRole: OrgRole | null;
  orgId: string | null;
  appRoles: AppRole[];
  appRolesDbOnly: AppRole[];
  orgAppRoles: AppRole[];
  orgAppRolesDbOnly: AppRole[];
  orgOnlyAppRolesDbOnly: AppRole[];
  isStaff: boolean;
  personId: string | null;
  capabilities: AccessCapabilities;
  roleScopes: RoleScope[];
  scopesReliable: boolean;
  /**
   * Whether the membership and role lookups actually succeeded. A permission error (401/403)
   * on either is swallowed so the app keeps working, but it leaves an EMPTY role set that is
   * not proof the viewer has no roles. Schema errors (the table isn't deployed) stay reliable,
   * as for scopes.
   */
  rolesReliable: boolean;
}

export function useAccessContext({ refreshAccess = false }: AccessContextOptions = {}) {
  const { user, session } = useAuth();
  // An access answer computed without the organization finds no membership, and route gates
  // treat it as final (#7231), so the lookup waits until the organization is chosen.
  const { organizationId, isResolving: organizationResolving } = useOrganization();
  const principal = JSON.stringify([organizationId, user?.id]);
  const [checkedPrincipal, setCheckedPrincipal] = useState<string | null>(null);

  // ── Phase 1: Core context (parallel queries, no serial wait) ──────────
  const coreQuery = useQuery({
    // access_token in the queryKey ensures roles are re-fetched on token
    // refresh (picks up claim changes). placeholderData keeps the previous
    // result visible during the re-fetch so the routing stack doesn't flash
    // a loader on every token renewal.
    queryKey: ["access-context-core", organizationId, user?.id, session?.access_token],
    // Only a refresh within the same principal may reuse access data. A user
    // switch can arrive as SIGNED_IN without an intervening sign-out render.
    placeholderData: (previousData, previousQuery) =>
      user?.id &&
      previousQuery?.queryKey[1] === organizationId &&
      previousQuery?.queryKey[2] === user.id
        ? previousData
        : undefined,
    retry: (failureCount, error) => {
      if (error instanceof AccessContextTimeoutError) return false;
      return failureCount < 3;
    },
    queryFn: async (): Promise<CoreAccessData | null> => {
      if (!user?.id) return null;

      const orgMembershipQuery = organizationId
        ? supabase
            .from("organization_members")
            .select("organization_id, role")
            .eq("user_id", user.id)
            .eq("organization_id", organizationId)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null });

      const [membershipResult, roleResult, personResult, scopeResult] =
        await withAccessContextTimeout(
          Promise.all([
            orgMembershipQuery,
            supabase.from("user_roles").select("role, organization_id").eq("user_id", user.id),
            organizationId
              ? supabase
                  .from("people")
                  .select("id")
                  .eq("user_id", user.id)
                  .eq("organization_id", organizationId)
                  .maybeSingle()
              : Promise.resolve({ data: null, error: null }),
            supabase
              .from("role_scopes" as any)
              .select("*")
              .eq("user_id", user.id),
          ]),
        );

      if (membershipResult.error &&
        !isSupabaseSchemaError(membershipResult.error) &&
        !isSupabasePermissionError(membershipResult.error)) {
        throw membershipResult.error;
      }
      if (roleResult.error &&
        !isSupabaseSchemaError(roleResult.error) &&
        !isSupabasePermissionError(roleResult.error)) {
        throw roleResult.error;
      }
      if (personResult.error &&
        !isSupabaseSchemaError(personResult.error) &&
        !isSupabasePermissionError(personResult.error)) {
        console.warn("People lookup for access context failed:", personResult.error);
      }
      // A scope-lookup failure must NOT silently widen access. Scopes are
      // reliable only when the query succeeded, OR when role_scopes simply isn't
      // deployed (schema error) — in which case nobody is scoped and org-wide is
      // the correct backward-compatible default. A permission or other error
      // means we could not read this user's scopes, so security gates that key on
      // scopeCount must fail closed (see hasOrgWideReportAccess).
      const scopesReliable = !scopeResult.error || isSupabaseSchemaError(scopeResult.error);
      // The same rule for the viewer's own membership and roles: a swallowed permission error
      // means we couldn't read them, not that there are none.
      const rolesReliable =
        !(membershipResult.error && isSupabasePermissionError(membershipResult.error)) &&
        !(roleResult.error && isSupabasePermissionError(roleResult.error));
      if (scopeResult.error &&
        !isSupabaseSchemaError(scopeResult.error) &&
        !isSupabasePermissionError(scopeResult.error)) {
        console.warn("Role scopes lookup failed:", scopeResult.error);
      }

      const metadataRoles = getRolesFromMetadata(
        session?.access_token,
        (user.user_metadata ?? {}) as Record<string, unknown>,
        (user.app_metadata ?? {}) as Record<string, unknown>
      );
      const fetchedRoles = normalizeRoles(roleResult.data?.map((role: any) => role.role) || []);
      const appRoles = Array.from(new Set([...metadataRoles, ...fetchedRoles]));
      const orgRole = (membershipResult.data?.role as OrgRole | undefined) ?? null;
      const orgId = membershipResult.data?.organization_id ?? organizationId ?? null;
      // Org-scoped app roles: DB user_roles rows scoped to the active org (or
      // global null-org grants), mirroring public.has_role_in_org. Only the
      // JWT roles that server authz honors without a user_roles row are copied
      // from metadata; platform_readonly still needs a DB grant for RLS.
      const orgScopedFetchedRoles = normalizeRoles(
        (roleResult.data ?? [])
          .filter((r: any) => r.organization_id == null || r.organization_id === orgId)
          .map((r: any) => r.role),
      );
      // Rows scoped to exactly the active org, without the global null-org grants.
      const orgOnlyFetchedRoles = normalizeRoles(
        ((roleResult.data ?? []) as Array<{ role: string; organization_id: string | null }>)
          .filter((r) => orgId != null && r.organization_id === orgId)
          .map((r) => r.role),
      );
      const trustedMetadataOrgRoles = metadataRoles.filter((role) => JWT_TRUSTED_ORG_ROLES.includes(role));
      const orgAppRoles = Array.from(new Set([...orgScopedFetchedRoles, ...trustedMetadataOrgRoles]));
      const isStaff =
        appRoles.some((role) => STAFF_APP_ROLES.includes(role)) ||
        orgRole === "owner" ||
        orgRole === "admin";
      const roleScopes = ((scopeResult.data ?? []) as unknown as RoleScope[]);
      const capabilities = buildCapabilities(appRoles, orgRole, roleScopes, orgId);

      return {
        orgRole,
        orgAppRolesDbOnly: orgScopedFetchedRoles,
        orgOnlyAppRolesDbOnly: orgOnlyFetchedRoles,
        appRolesDbOnly: fetchedRoles,
        orgId,
        appRoles,
        orgAppRoles,
        isStaff,
        personId: personResult.data?.id ?? null,
        capabilities,
        roleScopes,
        scopesReliable,
        rolesReliable,
      };
    },
    enabled: !!user?.id && !organizationResolving,
    // A foreground, successful refresh detects DB changes within this interval plus fetch
    // latency. Background suspension, offline/errors and unchanged JWT claims have no bound.
    ...(refreshAccess ? {
      refetchOnWindowFocus: "always" as const,
      refetchOnReconnect: "always" as const,
      refetchInterval: ACCESS_CONTEXT_REFRESH_INTERVAL_MS,
      refetchIntervalInBackground: false,
    } : {}),
  });

  const refetchAccess = coreQuery.refetch;
  useEffect(() => {
    if (!refreshAccess || !user?.id || organizationResolving) {
      setCheckedPrincipal(null);
      return;
    }
    let active = true;
    // A fresh five-minute cached answer still needs verification when this observer activates.
    // Join any request already started by another observer rather than cancel and restart it.
    void refetchAccess({ cancelRefetch: false }).then(() => {
      if (active) setCheckedPrincipal(principal);
    });
    return () => { active = false; };
    // Token renewal keeps the same verified principal; its existing query-key refresh need not
    // retire a workspace whose eventual access fingerprint is unchanged.
  }, [refreshAccess, user?.id, organizationResolving, principal, refetchAccess]);

  const verificationPending = refreshAccess && !!user?.id && checkedPrincipal !== principal;

  const core = coreQuery.data ?? null;
  // A fingerprint of what RLS decides this viewer's reads by: organization, role, roles (with both
  // DB-only sets: bare has_any_role() policies, people_select_authenticated among them, read the
  // global DB-backed one) and active-organization role scopes. Stable across token refreshes; it changes when any of them do,
  // including a narrowing that keeps a permission (org-wide staff becoming ministry-scoped).
  // Ministry memberships load in the background after the core settles, so they are left out:
  // including them would change the revision moments after every load.
  const accessRevision = useMemo(() => {
    if (!core) return null;
    const sorted = (values: readonly string[]) => [...values].sort();
    return JSON.stringify([
      core.orgId,
      core.orgRole,
      sorted(core.appRoles),
      sorted(core.appRolesDbOnly),
      sorted(core.orgAppRoles),
      sorted(core.orgAppRolesDbOnly),
      sorted(core.roleScopes
        .filter((scope) => scope.organization_id === organizationId)
        .map((scope) => `${scope.role}|${scope.scope_entity_type}|${scope.scope_entity_id}|${scope.organization_id}`)),
      core.scopesReliable,
      core.rolesReliable,
    ]);
  }, [core, organizationId]);

  // ── Phase 2: Ministry memberships (deferred, non-blocking) ────────────
  // This query fires once core resolves with a personId, but does NOT block
  // ProtectedRoute or the isLoading flag. Ministry data populates
  // asynchronously and downstream consumers (MinistryDetail, VolunteerShell,
  // useLeaderMinistries) simply start with empty arrays until it loads.
  const ministryQuery = useQuery({
    queryKey: ["access-context-ministry", core?.personId, organizationId],
    queryFn: async (): Promise<MinistryMembership[]> => {
      if (!core?.personId || !organizationId) return [];
      try {
        const { data, error } = await supabase
          .from("ministry_memberships")
          .select("id, ministry_id, role")
          .eq("person_id", core.personId)
          .eq("org_id", organizationId)
          .eq("status", "active");
        if (!error && data) {
          return data.map((m) => ({
            id: m.id,
            ministry_id: m.ministry_id,
            role: m.role,
            is_leader: m.role === "leader" || m.role === "scheduler",
          }));
        }
        if (error && !isSupabaseSchemaError(error) && !isSupabasePermissionError(error)) {
          console.warn("Ministry memberships query failed:", error.message);
        }
      } catch (err) {
        if (!isSupabaseSchemaError(err) && !isSupabasePermissionError(err)) {
          console.warn("Ministry memberships lookup failed:", err);
        }
      }
      return [];
    },
    enabled: !!core?.personId && !!organizationId,
  });

  const ministryMemberships = ministryQuery.data ?? [];

  // ── Compose final AccessContext ────────────────────────────────────────
  const fallbackCapabilities = useMemo(() => buildCapabilities([], null), []);

  const leaderMinistries = useMemo(
    () =>
      Array.from(
        new Set(
          ministryMemberships
            .filter(isLeaderMembership)
            .map((m) => m.ministry_id),
        ),
      ),
    [ministryMemberships],
  );

  const memberMinistries = useMemo(
    () => Array.from(new Set(ministryMemberships.map((m) => m.ministry_id))),
    [ministryMemberships],
  );

  const data: AccessContext | null = core
    ? {
        org_id: core.orgId,
        org_role: core.orgRole,
        app_roles: core.appRoles,
        app_roles_db_only: core.appRolesDbOnly,
        org_app_roles: core.orgAppRoles,
        org_app_roles_db_only: core.orgAppRolesDbOnly,
        org_only_app_roles_db_only: core.orgOnlyAppRolesDbOnly,
        is_staff: core.isStaff,
        ministry_memberships: ministryMemberships,
        leader_ministries: leaderMinistries,
        member_ministries: memberMinistries,
        capabilities: core.capabilities,
        scopedEntities: core.roleScopes,
        scopedEntitiesReliable: core.scopesReliable,
      }
    : null;

  const queryNotYetEnabled =
    !!user?.id &&
    coreQuery.isPending &&
    !coreQuery.data;

  const fallbackData: AccessContext = {
    org_id: null,
    org_role: null,
    app_roles: [],
    app_roles_db_only: [],
    org_app_roles: [],
    org_app_roles_db_only: [],
    org_only_app_roles_db_only: [],
    is_staff: false,
    ministry_memberships: [],
    leader_ministries: [],
    member_ministries: [],
    capabilities: fallbackCapabilities,
    scopedEntities: [],
    // No core data resolved (loading / no user / error) — not a trustworthy
    // "unrestricted" signal, so fail closed for scope-gated consumers.
    scopedEntitiesReliable: false,
  };

  const refreshUnavailable = refreshAccess && (
    coreQuery.isError || (core !== null && (!core.rolesReliable || !core.scopesReliable))
  );

  return {
    ...coreQuery,
    // isLoading only depends on the core query — ministry loads in background
    isLoading: coreQuery.isLoading || queryNotYetEnabled || (verificationPending && !refreshUnavailable),
    data: verificationPending || refreshUnavailable ? fallbackData : data ?? fallbackData,
    // The roles never loaded (timeout, 5xx), or a 401/403 on the viewer's own membership or
    // role rows was swallowed into an empty set: either way the empty roles are not proof the
    // viewer lacks access. Default consumers keep the last good roles on a failed refresh;
    // an opted-in workspace fails closed when its current access cannot be verified.
    rolesUnavailable: refreshUnavailable || (coreQuery.isError && !core) || (core !== null && !core.rolesReliable),
    /** See above; null until the core access has loaded. */
    accessRevision: verificationPending || refreshUnavailable ? null : accessRevision,
  };
}
